import { useEffect, useState } from 'react'
import { Eye } from 'lucide-react'
import DataTable from '../../components/DataTable'
import Drawer from '../../components/Drawer'
import { listAwarenessViews } from './engagementService'
import { awarenessStrings as S } from './strings'

/**
 * Who has opened one bulletin.
 *
 * `POST /api/admin/awareness-management/views` has always answered a page of every learner
 * who read a bulletin — username, e-mail and timestamp — and nothing in the client called
 * it. `engagementService`'s own header documented `listViews` and never implemented it, and
 * the awareness table's columns are title, user, created-on and active. So an administrator
 * could publish a bulletin and had no way to learn whether a single person had read it.
 *
 * A drawer rather than a column, for the same reason `PolicyUsersDrawer` is one: the answer
 * is a list of people, which does not fit in a cell, and the bulletin list stays where it is
 * while you look.
 */
export default function AwarenessViewsDrawer({ open, awareness, onClose }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [failure, setFailure] = useState('')

  const awarenessId = awareness?.id

  useEffect(() => {
    if (!open || !awarenessId) return undefined

    let cancelled = false
    setLoading(true)
    setFailure('')

    listAwarenessViews(awarenessId)
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        setFailure(response.message ?? '')
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setRows([])
        setFailure(S.views.failed)
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, awarenessId])

  const columns = [
    { key: 'name', header: S.views.columns.name, render: (row) => <bdi>{row.name}</bdi> },
    {
      key: 'username',
      header: S.views.columns.username,
      render: (row) => (
        <span className="ud__date" dir="ltr">
          {row.username}
        </span>
      ),
    },
    {
      key: 'email',
      header: S.views.columns.email,
      render: (row) => (
        <span className="ud__date" dir="ltr">
          {row.email}
        </span>
      ),
    },
    {
      key: 'seenOn',
      header: S.views.columns.seenOn,
      // Formatted server-side, and under ar-SA that is the Hijri date. Printed, never parsed.
      render: (row) => (
        <span className="ud__date" dir="ltr">
          {row.seenOn}
        </span>
      ),
    },
  ]

  return (
    <Drawer
      dialog
      open={open}
      size="lg"
      title={S.views.title}
      icon={Eye}
      subtitle={awareness?.title}
      onClose={onClose}
    >
      <DataTable
        /* A list inside a sheet is opened to answer one question; it starts at the top. */
        stateKey={false}
        columns={columns}
        rows={rows}
        loading={loading}
        error={failure ? { message: failure } : null}
        controls={false}
        getRowId={(row) => row.id}
        emptyMessage={S.views.empty}
      />
    </Drawer>
  )
}
